import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { ArrowRight } from 'lucide-react'
import MarketingNav from './MarketingNav'
import MediaSlot from './MediaSlot'
import SiteFooter from './SiteFooter'
import './marketing-dark.css'
import { slugFor, clusterFor } from './marketingMeta'
import { CATALOG, byKey } from '../lib/serviceCatalog'
import { getSession } from '../lib/session'
import { cartCount } from '../lib/cart'

/**
 * UseCasesIndex — the public /use-cases index for ai.reright.io.
 *
 * A simple, dark `.mkt` list of the high-intent buyer scenarios, each linking to
 * its /use-cases/<slug> page. Fully HEADLESS: the list comes from
 * GET /api/v1/site/usecases and never throws — a cold API just yields an empty
 * grid inside the shared shell. Reuses <MarketingNav>/<MediaSlot>/<SiteFooter>
 * exactly like <ProductsIndex>, so the whole site reads as one system.
 */

// Product catalog fallback for the nav mega-dropdown (independent of use-cases).
const DEFAULT_SERVICES = CATALOG.map((s) => ({
  key: s.key,
  slug: slugFor(s.key),
  cluster: clusterFor(s.key),
  keyword: s.name,
  name: s.name,
  short: s.short,
  price: s.price,
  icon: s.icon,
}))

export default function UseCasesIndex() {
  const navigate = useNavigate()
  const authed = !!getSession()?.apiKey

  const [services, setServices] = useState(DEFAULT_SERVICES)
  const [usecases, setUsecases] = useState([])
  const [meta, setMeta] = useState({})
  const [seatCount, setSeatCount] = useState(cartCount())

  // Live cart badge sync (shared nav shell).
  useEffect(() => {
    const sync = () => setSeatCount(cartCount())
    sync()
    window.addEventListener('reright-cart', sync)
    return () => window.removeEventListener('reright-cart', sync)
  }, [])

  // Product catalog → the nav mega-dropdown.
  useEffect(() => {
    let alive = true
    axios.get('/api/v1/site/catalog')
      .then(({ data }) => {
        if (!alive || !data || !Array.isArray(data.services) || !data.services.length) return
        setServices(data.services.map((s) => ({ ...s, icon: byKey(s.key)?.icon })))
      })
      .catch(() => { /* keep defaults */ })
    return () => { alive = false }
  }, [])

  // Use-case list + index meta. Never throws — an empty list still renders.
  useEffect(() => {
    let alive = true
    axios.get('/api/v1/site/usecases')
      .then(({ data }) => {
        if (!alive || !data) return
        if (Array.isArray(data.usecases)) setUsecases(data.usecases)
        if (data.meta && typeof data.meta === 'object') setMeta(data.meta)
      })
      .catch(() => { /* leave empty */ })
    return () => { alive = false }
  }, [])

  // SEO — headless title + meta description.
  useEffect(() => {
    document.title = meta.title || 'AI Use Cases | ReRight'
    setMetaDescription(meta.description || '')
  }, [meta])

  const goCart = useCallback(() => navigate('/cart'), [navigate])
  const signIn = { to: authed ? '/' : '/signin', label: authed ? 'Open console' : 'Sign in', router: true }

  // Map each use-case's mapped service slug → its display name (for the card tag).
  const svcName = {}
  for (const s of services) svcName[s.slug] = s.name || s.keyword

  return (
    <div className="mkt min-h-screen bg-gray-50 text-gray-900 antialiased">
      <MarketingNav services={services} cartCount={seatCount} onOpenCart={goCart} signIn={signIn} />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="bg-gray-50 border-b border-gray-200 overflow-hidden">
        <div className="max-w-4xl mx-auto px-5 pt-20 sm:pt-28 pb-14 text-center">
          <Eyebrow center>Buyer scenarios · mapped to real services</Eyebrow>
          <h1 className="text-[2.6rem] sm:text-6xl lg:text-[4.2rem] font-display font-black tracking-tight leading-[1.02] mt-5 text-gray-900" style={{ textWrap: 'balance' }}>
            {meta.h1 || 'AI use cases'}
          </h1>
          <p className="text-[17px] sm:text-xl text-gray-500 mt-6 max-w-2xl mx-auto leading-relaxed">
            {meta.intro || 'Real buyer questions, answered honestly and mapped to the ReRight service that solves each one. Pick the scenario that fits, then see the product behind it.'}
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Cta href="#scenarios" variant="primary" onHash={scrollToId}>Explore use cases</Cta>
            <Cta href="/products" variant="secondary">Browse all services</Cta>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-5 pb-20 sm:pb-24">
          <MediaSlot
            image={meta.heroImage || ''}
            video={meta.heroVideo || ''}
            kind="video"
            label="Use cases overview"
            sublabel="A short tour of the scenarios · add a video in the CMS"
          />
        </div>
      </section>

      {/* ── The scenarios ─────────────────────────────────────────────────── */}
      <section id="scenarios" className="scroll-mt-16 bg-gray-100 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-5 py-20 sm:py-24">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <Eyebrow>Use cases</Eyebrow>
              <h2 className="text-3xl sm:text-4xl font-display font-black tracking-tight text-gray-900 mt-3" style={{ textWrap: 'balance' }}>
                A scenario for every intent.
              </h2>
              <p className="text-[16px] text-gray-500 mt-3 leading-relaxed max-w-2xl">Each page answers a real buyer question and links straight to the service that delivers it.</p>
            </div>
            <span className="hidden sm:inline-flex shrink-0 text-[13px] font-semibold text-gray-400 tabular-nums">
              {usecases.length} use case{usecases.length === 1 ? '' : 's'}
            </span>
          </div>

          {usecases.length === 0 ? (
            <p className="text-sm text-gray-500 mt-12">Use cases are loading — check back in a moment.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
              {usecases.map((u) => (
                <UseCaseCard key={u.slug} u={u} serviceName={svcName[u.service]} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Closing CTA band ──────────────────────────────────────────────── */}
      <section className="bg-gray-50">
        <div className="max-w-6xl mx-auto px-5 py-24 text-center">
          <h2 className="text-3xl sm:text-5xl font-display font-black tracking-tight text-gray-900" style={{ textWrap: 'balance' }}>
            Find your scenario, switch on the service.
          </h2>
          <p className="text-[17px] sm:text-xl text-gray-500 mt-4 max-w-2xl mx-auto">
            Every use case maps to a real ReRight service. Bring your content, switch on what you need, pay only for what you turn on.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Cta href="/products" variant="primary">Browse all services</Cta>
            <Cta href="/guide" variant="secondary">Read the guide</Cta>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  )
}

// ── Use-case card (links to /use-cases/<slug>) ───────────────────────────────
function UseCaseCard({ u, serviceName }) {
  const name = u.name || u.keyword || u.title || u.slug
  return (
    <a
      href={`/use-cases/${u.slug}`}
      className="group relative flex flex-col text-left bg-gray-50 rounded-2xl border border-gray-200 p-6 hover:border-gray-300 hover:shadow-[0_16px_40px_-24px_rgba(15,23,42,0.35)] hover:-translate-y-0.5 transition-all duration-200"
    >
      {serviceName && (
        <span className="inline-flex self-start items-center gap-1 rounded-full bg-primary-500/10 text-primary-600 text-[11px] font-semibold px-2.5 py-1">
          {serviceName}
        </span>
      )}
      <h3 className="font-bold text-gray-900 mt-4 text-[17px] tracking-tight leading-snug">{u.h1 || name}</h3>
      <p className="text-[13.5px] text-gray-500 mt-1.5 leading-relaxed flex-1">{u.short}</p>
      <span className="mt-4 text-[13px] font-semibold inline-flex items-center gap-1 text-primary-600">
        Read the answer <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
      </span>
    </a>
  )
}

// ── Small shared pieces (mirror ProductsIndex) ───────────────────────────────
function setMetaDescription(text) {
  if (typeof document === 'undefined') return
  let tag = document.querySelector('meta[name="description"]')
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute('name', 'description')
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', String(text || ''))
}

function scrollToId(href) {
  const el = typeof href === 'string' && href.startsWith('#') ? document.querySelector(href) : null
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function Eyebrow({ children, center }) {
  return (
    <p className={`inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-primary-600 ${center ? 'justify-center' : ''}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-primary-600" aria-hidden="true" />{children}
    </p>
  )
}

function Cta({ href = '#', variant = 'primary', onHash, children }) {
  const base = 'inline-flex items-center justify-center gap-1.5 rounded-full text-[15px] font-semibold px-6 py-3 transition-all duration-200'
  const style = variant === 'primary'
    ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-sm hover:shadow'
    : 'border border-gray-300 text-gray-900 hover:border-gray-400 hover:bg-gray-100'
  const cls = `${base} ${style}`
  if (typeof href === 'string' && href.startsWith('#')) {
    return <a href={href} className={cls} onClick={(e) => { e.preventDefault(); onHash && onHash(href) }}>{children}</a>
  }
  return <a href={href} className={cls}>{children}</a>
}
